/**
 * Validación del formulario público de sorteo (feria) — internacional.
 *
 * Payload:
 * - nombre = `${nombres} ${apellidos}`
 * - telefono / whatsapp = mismo celular normalizado
 * - pais / departamento (región) / ciudad
 */

export const SORTEO_PAIS_DEFAULT = "Bolivia";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_TEXT = 2;
const MAX_TEXT = 80;
const MIN_PHONE_DIGITS = 7;
const MAX_PHONE_DIGITS = 15;

/**
 * Quita espacios, guiones y paréntesis; conserva dígitos y '+' inicial.
 */
export function normalizeInternationalPhoneInput(raw) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return "";

  let out = "";
  for (const char of trimmed) {
    if (/\d/.test(char)) {
      out += char;
    } else if (char === "+" && out.length === 0) {
      out += char;
    }
  }
  return out;
}

/**
 * 7–15 dígitos; conserva '+' si viene. No infiere país.
 */
export function isValidInternationalPhone(raw) {
  const norm = normalizeInternationalPhoneInput(raw);
  if (!norm) return false;
  const hasPlus = norm.startsWith("+");
  const digits = hasPlus ? norm.slice(1) : norm;
  if (digits.length < MIN_PHONE_DIGITS || digits.length > MAX_PHONE_DIGITS) {
    return false;
  }
  return /^\d+$/.test(digits);
}

function resolveFreeText(raw, { required = true } = {}) {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) {
    return required ? { value: "", ok: false } : { value: "", ok: true };
  }
  if (trimmed.length < MIN_TEXT || trimmed.length > MAX_TEXT) {
    return { value: trimmed, ok: false };
  }
  return { value: trimmed, ok: true };
}

export function validateSorteoForm(raw) {
  const nombres = String(raw.nombres ?? "").trim();
  const apellidos = String(raw.apellidos ?? "").trim();
  const email = String(raw.email ?? "").trim();
  const whatsappCelular = normalizeInternationalPhoneInput(
    raw.whatsappCelular ?? raw.whatsapp ?? raw.telefono ?? ""
  );

  const errors = {};

  if (!nombres) {
    errors.nombres = "Los nombres son obligatorios.";
  } else if (nombres.length < MIN_TEXT) {
    errors.nombres = "Los nombres deben tener al menos 2 caracteres.";
  }

  if (!apellidos) {
    errors.apellidos = "Los apellidos son obligatorios.";
  } else if (apellidos.length < MIN_TEXT) {
    errors.apellidos = "Los apellidos deben tener al menos 2 caracteres.";
  }

  if (!email) {
    errors.email = "El correo es obligatorio.";
  } else if (!EMAIL_RE.test(email)) {
    errors.email = "Ingresa un correo válido.";
  }

  if (!whatsappCelular) {
    errors.whatsappCelular = "El WhatsApp / celular es obligatorio.";
  } else if (!isValidInternationalPhone(whatsappCelular)) {
    errors.whatsappCelular =
      "Ingresa un número válido (7 a 15 dígitos; se permite +).";
  }

  const paisRaw = String(raw.pais ?? "").trim();
  const paisResolved = paisRaw
    ? resolveFreeText(paisRaw)
    : { value: SORTEO_PAIS_DEFAULT, ok: true };
  if (!paisResolved.ok) {
    errors.pais = "El país debe tener entre 2 y 80 caracteres.";
  }

  const departamento = resolveFreeText(raw.departamento);
  if (!departamento.ok) {
    errors.departamento =
      departamento.value === ""
        ? "Selecciona o escribe tu departamento / estado / región."
        : "Departamento / Estado / Región debe tener entre 2 y 80 caracteres.";
  }

  const ciudad = resolveFreeText(raw.ciudad);
  if (!ciudad.ok) {
    errors.ciudad =
      ciudad.value === ""
        ? "La ciudad / municipio es obligatoria."
        : "La ciudad / municipio debe tener entre 2 y 80 caracteres.";
  }

  const values = {
    nombre: `${nombres} ${apellidos}`.trim(),
    email,
    telefono: whatsappCelular,
    whatsapp: whatsappCelular,
    pais: paisResolved.value,
    departamento: departamento.value,
    ciudad: ciudad.value,
  };

  return {
    values,
    errors,
    isValid: Object.keys(errors).length === 0,
  };
}
